<template>
    <div class="space-y-4 flex flex-col">

        <label
            v-if="$slots.default || inputLabel"
            :for="'input' + _uid"
            class="text-body-sm font-semibold text-black"
        >
            <slot>{{ capitaliseFirst(inputLabel) }}</slot>
        </label>

        <div v-if="$slots.tip" class="text-semantic-info flex items-start gap-4">
            <svg-icon type="info" class="w-18 h-18 mt-2" />
            <slot name="tip"></slot>
        </div>

        <p v-if="error" class="block text-semantic-error">
            <span class="inline-flex items-center gap-4">
                <svg-icon
                    type="error"
                    class="w-16 h-16"
                />
                <span class="text-body-sm">{{ capitaliseFirst(error) }}</span>
            </span>
        </p>

        <div class="flex-1">
            <div
                class="group rounded flex overflow-hidden border has-[:focus]:border-semantic-info h-full"
                :class="[disabled ? 'border-primary-20 bg-primary-5 text-primary-60' : error ? 'border-semantic-error' : 'border-primary-80']"
                @click="focus"
            >
                <svg-icon
                    class="self-center w-18 h-18 ml-16"
                    :class="{'text-primary-60': disabled}"
                    :type="icon"
                    v-if="icon"
                />

                <div
                    v-if="prefix"
                    :class="{'ml-16': !icon, 'mr-4': !noSpacingBefore}"
                    class="text-primary-80 cursor-default flex items-center flex-none flex-nowrap"
                >
                    {{ prefix }}
                </div>

                <slot name="before"></slot>

                <input
                    :id=" staticId ? staticId : 'input' + _uid"
                    ref="input"
                    v-model="inputValue"
                    :class="[disabled ? 'cursor-not-allowed' : '', {'pl-16': !icon && !prefix && !noSpacingBefore, 'pl-8': (icon || prefix) && !noSpacingBefore, 'pr-16': !emojis && !$slots.after, 'pr-4': emojis || $slots.after}]"
                    :disabled="disabled"
                    :readonly="readonly"
                    :max="max"
                    :min="min"
                    :step="step"
                    :name="name"
                    :placeholder="placeholder"
                    :required="required"
                    :type="type"
                    :autocomplete="autocomplete"
                    class="h-full flex-grow outline-none py-12 placeholder-primary-80"
                    @input="handleInput"
                    @keydown="$emit('keydown', $event)"
                    @keyup.esc="$emit('escape')"
                    @blur="handleChange($event.target.value)"
                    @change="$event.target.blur()"
                    @keydown.enter.prevent="$emit('enter', $event.target.value)"
                >

                <div v-if="emojis" class="my-auto px-8">
                    <emoji-button @select="insertEmoji"></emoji-button>
                </div>

                <div
                    v-if="$slots.after"
                    class="flex items-center px-8"
                    :class="{'bg-primary text-white': coloredAfter}"
                >
                    <slot name="after"></slot>
                </div>
            </div>
        </div>

        <div v-if="maxSize"
             :class="{'text-semantic-error font-semibold' : inputCharacterLength >= maxSize}"
             class="flex justify-between">
            <p class="text-semantic-error text-body-sm">{{ customError }}</p>
            <div>
                {{ inputCharacterLength }} / {{ maxSize }}
            </div>
        </div>
    </div>
</template>

<script>
import SvgIcon from '../ui/SvgIcon.vue';
import EmojiButton from '../ui/EmojiButton.vue';
import {ucfirst} from '../../../shared/helpers';

export default {
    components: {
        'emoji-button': EmojiButton,
        'svg-icon': SvgIcon,
    },
    props: {
        staticId: [String, Number],
        value: {
            default: '',
        },
        placeholder: {
            default: '',
        },
        type: {
            default: 'text',
            validator: value => ['number', 'text', 'email', 'password', 'search'].includes(value),
        },
        step: {
            default: 1,
            type: Number
        },
        prefix: String,
        icon: String,
        name: String,
        autofocus: Boolean,
        disabled: Boolean,
        readonly: Boolean,
        emojis: Boolean,
        required: Boolean,
        autocomplete: {
            type: String,
            default: 'on'
        },
        min: Number,
        max: Number,
        errors: Array,
        maxSize: {
            required: false,
            default: null,
        },
        inputLabel: String,
        coloredAfter: Boolean,
        noSpacingBefore: Boolean,
    },
    data: function () {
        return {
            customError: '',
        };
    },
    computed: {
        inputValue: {
            get() {
                return this.value;
            },
            set(value) {
                if (this.disabled) return;
                if (value.length >= this.maxSize) {
                    this.customError = this.$t('general.max_characters', {amount: this.maxSize});
                }else if(value.length > this.maxSize){
                   return;
                }else{
                    this.customError = '';
                }

                if (this.type === 'number') {
                    this.$emit('input', parseInt(value));
                } else {
                    this.$emit('input', value);
                }
            },
        },
        error() {
            if (!this.errors) {
                return null;
            }

            return Array.isArray(this.errors) ? this.errors[0] : this.errors;
        },
        inputCharacterLength() {
            return (this.inputValue ? this.inputValue.length : 0);
        },
    },
    methods: {
        focus() {
            if (this.$refs.input || null) {
                this.$refs.input.focus();
            }
        },
        insertEmoji(emoji) {

            let start = this.$refs.input.selectionStart;
            let end = this.$refs.input.selectionEnd;

            this.inputValue = this.inputValue.slice(0, start) + emoji + this.inputValue.slice(end);

            this.focus();
        },
        capitaliseFirst(text = '') {
            return ucfirst(text);
        },
        handleInput(e) {
            // Hold up, we're gonna have to convert our input to a contenteditable
            // container if we want to be able to add an autocomplete dropdown for
            // emojis on typing ":".

            // couple of gotchas:
            // -- handle @paste event by sanitizing input replace(/\r?\n|\r/g, '')
            // -- Can't use two way data binding because it'll reset the caret position

            // let start = this.$refs.input.selectionStart;
            // let end = this.$refs.input.selectionEnd;

            // if (start !== end) {
            //     return;
            // }

            // let before = e.target.value.slice(0, start);
            // before = before.slice(before.lastIndexOf(' ') + 1);

            // console.log(/^:[a-z_]{2,}$/.test(before));
        },
        handleChange(input) {
            this.$emit('input-blur', input);
        },
    },
    mounted() {
        if (this.autofocus) {
            this.focus();
        }
    },
};
</script>

<style scoped>
input {
    min-width: 1px;
}
</style>
